// Comparison + cost computation (site logic mirrored from the Google Sheet)
export const num = (v) => {
  const n = typeof v === "string" ? parseFloat(v.replace(",", ".")) : Number(v);
  return Number.isFinite(n) ? n : 0;
};

export const DEFAULT_PARAMS = {
  area_m2: 100,
  notch: 6,
  thickness: 10,
  // grout (stucchi per fughe) geometry
  tileA: 30,
  tileB: 60,
  tileThickness: 10,
  jointWidth: 3,
  salary: 0,
  transportMin: 0,
  mixingMin: 0,
  palletMin: 0,
};

export const DEFAULT_GROUT_DENSITY = 1.6; // kg/dm3 fallback

// how consumption is derived per subcategory
export const CONSUMPTION_MODE = {
  adesivi_standard: "notch", adesivi_rapidi: "notch", adesivi_reattivi: "notch",
  colle_parquet: "notch", colle_pvc_lvt: "notch",
  autolivellanti: "thickness", massetti: "thickness", rasanti: "thickness",
  rasature_specifiche: "thickness", ripristino_edilizia: "thickness",
  ripristino_stradale: "thickness", malte_elastiche: "flat",
  primer: "flat", imperm_liquidi: "flat", stucchi_fughe: "grout",
  teli_accessori: "flat", protezione_ferri: "flat", ancoraggi_sigillanti: "flat",
};

export const modeFor = (subcategory) => CONSUMPTION_MODE[subcategory] || "notch";

// per-product mode: a product can override its subcategory default via spec.cost_model (e.g. "pack")
export const modeForProduct = (spec, subcategory) =>
  (spec && spec.cost_model) ? spec.cost_model : modeFor(subcategory);

// pack model: products sold per package (cartridge/sausage/roll) with a coverage per pack
export function computePack(spec, qty) {
  const price = num(spec.pack_price);
  const coverage = num(spec.pack_coverage);
  const q = num(qty);
  const packs = coverage ? q / coverage : 0;
  const total = packs * price;
  const costPerUnit = coverage ? price / coverage : 0;
  return { mode: "pack", packs, price, coverage, total, costPerUnit, unit: spec.pack_unit || "pz" };
}

export function isPackComputable(spec) {
  return num(spec.pack_price) > 0 && num(spec.pack_coverage) > 0;
}

// consumption in kg/m2: notch -> base*(notch/6); thickness -> base*mm; flat -> base
// grout -> geometry: ((A+B)/(10*A*B)) * tileThickness * jointWidth * density
export function groutConsumption(spec, params) {
  const A = num(params.tileA), B = num(params.tileB);
  const D = num(params.tileThickness), F = num(params.jointWidth);
  if (A <= 0 || B <= 0 || D <= 0 || F <= 0) return 0;
  const density = num(spec.densita_kg_dm3) || DEFAULT_GROUT_DENSITY;
  return ((A + B) / (10 * A * B)) * D * F * density;
}

// yield table: exact manufacturer consumption per notch/thickness (mm).
// Priority over the linear model; interpolates/extrapolates for missing points.
export function tableConsumption(table, mm) {
  const pts = Object.keys(table || {})
    .map((k) => [num(k), num(table[k])])
    .filter((p) => p[0] > 0 && p[1] > 0)
    .sort((a, b) => a[0] - b[0]);
  if (!pts.length) return 0;
  const exact = pts.find((p) => p[0] === mm);
  if (exact) return exact[1];
  let lower = null, upper = null;
  for (const p of pts) {
    if (p[0] <= mm) lower = p;
    if (p[0] >= mm && !upper) upper = p;
  }
  if (lower && upper && lower[0] !== upper[0]) {
    const t = (mm - lower[0]) / (upper[0] - lower[0]);
    return lower[1] + t * (upper[1] - lower[1]);
  }
  const ref = lower || upper;               // outside range -> proportional from nearest point
  return ref[0] ? ref[1] * (mm / ref[0]) : ref[1];
}

export function effectiveConsumption(spec, params) {
  // Optional user-entered yield (m²/bag): consumption derived directly from it
  const resaOv = num(spec.resa_override);
  if (resaOv > 0 && num(spec.bag_weight_kg) > 0) return num(spec.bag_weight_kg) / resaOv;
  // Waterproofing membranes: FULL dry thickness at 2 coats (no notched-trowel factor)
  const spess = num(spec.spessore_2mani_mm);
  if (spess > 0 && num(spec.consumption_kg_per_m2) > 0) return num(spec.consumption_kg_per_m2) * spess;
  const base = num(spec.consumption_kg_per_m2);
  const mode = (params && params.mode) || "notch";
  // Optional explicit physical model for notched trowels: base(per mm) × notch × teeth × inclination
  const mmBase = num(spec.consumo_teorico_mm);
  if (mmBase > 0 && mode === "notch") {
    const notch = num(params.notch) || 6;
    const cd = num(spec.coef_denti) || 0.5;
    const ci = num(spec.coef_inclinazione) || 0.8;
    return mmBase * notch * cd * ci;
  }
  // Manufacturer yield table has priority over the linear model
  if (spec.consumption_table && typeof spec.consumption_table === "object" && (mode === "notch" || mode === "thickness")) {
    const mm = mode === "thickness" ? (num(params.thickness) || 1) : (num(params.notch) || 6);
    const v = tableConsumption(spec.consumption_table, mm);
    if (v > 0) return v;
  }
  if (mode === "grout") return groutConsumption(spec, params);
  if (mode === "thickness") return base * (num(params.thickness) || 1);
  if (mode === "flat") return base;
  return base * ((num(params.notch) || 6) / 6);
}

// spec object may be overridden by user-editable values
export function computeProduct(spec, params) {
  // Pack products (cartridge / sausage / roll): economics driven by pack price + coverage (m²/pack)
  if (params && params.mode === "pack") {
    const coverage = num(spec.pack_coverage);
    const price = num(spec.pack_price);
    const area = num(params.area_m2);
    const salary = num(params.salary);
    const packs = coverage ? area / coverage : 0;
    const productCost = packs * price;
    const transportCost = (packs * num(params.transportMin)) / 60 * salary;
    const mixingCost = (packs * num(params.mixingMin)) / 60 * salary;
    const total = productCost + transportCost + mixingCost;
    return {
      totalKg: 0, bags: packs, productCost, transportCost, mixingCost, palletCost: 0,
      total, costPerM2: coverage ? price / coverage : 0, resa: coverage, consumption: 0, isPack: true,
    };
  }
  const consumption = effectiveConsumption(spec, params);
  const bagWeight = num(spec.bag_weight_kg);
  const pricePerKg = num(spec.price_per_kg);
  const bagsPerPallet = num(spec.bags_per_pallet) || 1;

  const area = num(params.area_m2);
  const salary = num(params.salary);

  const totalKg = area * consumption;
  const bags = bagWeight ? totalKg / bagWeight : 0;
  const productCost = totalKg * pricePerKg;

  const transportCost = (bags * num(params.transportMin)) / 60 * salary;
  const mixingCost = (bags * num(params.mixingMin)) / 60 * salary;
  const pallets = bagsPerPallet ? bags / bagsPerPallet : 0;
  const palletCost = (pallets * num(params.palletMin)) / 60 * salary;

  const total = productCost + transportCost + mixingCost + palletCost;
  const costPerM2 = area ? total / area : 0;
  const resa = consumption ? bagWeight / consumption : 0;

  return { totalKg, bags, productCost, transportCost, mixingCost, palletCost, total, costPerM2, resa, consumption };
}

export function isComputable(spec, mode) {
  if (mode === "pack") return num(spec.pack_price) > 0 && num(spec.pack_coverage) > 0;
  const priceOk = num(spec.price_per_kg) > 0 && num(spec.bag_weight_kg) > 0;
  if (mode === "grout") return priceOk;
  const consOk = num(spec.consumption_kg_per_m2) > 0
    || (num(spec.resa_override) > 0 && num(spec.bag_weight_kg) > 0)
    || ((mode === "notch" || mode === "thickness") && spec.consumption_table
        && typeof spec.consumption_table === "object"
        && Object.values(spec.consumption_table).some((v) => num(v) > 0));
  return priceOk && consOk;
}

export const chf = (v) =>
  "CHF " + num(v).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmt = (v, d = 2) =>
  num(v).toLocaleString("de-CH", { minimumFractionDigits: d, maximumFractionDigits: d });
